import { useEffect, useRef, useState } from 'react'
import { Avatar } from '../components/Avatar'
import {
  fetchPraesentationFolien,
  fetchPraesentationStand,
  fetchPraesentationVerlauf,
  praesentationAntwortTonUrl,
  praesentationBildUrl,
  praesentationTonUrl,
  reportPraesentationBildschirm,
  sendPraesentationAutoWeiter,
  type PraesentationFolie,
  type PraesentationNachricht,
  type PraesentationStand,
} from '../lib/api'

/** Wie oft der Bildschirm nachfragt und seinen Stand meldet. */
const POLL_MS = 1000
/** In der Fragerunde: wie oft nach neuen Antworten sehen. */
const VERLAUF_POLL_MS = 1500

/**
 * Die Präsentation auf dem großen Bildschirm (`/buehne?k=…`, Modus
 * „Präsentation“ der gemeinsamen Fernbedienung): Die Folie steht
 * bildschirmfüllend, K4ia spricht ihren Text — und wartet dann, bis am Handy
 * jemand weiterklickt (Flo, 18.10.: „nach jeder Folie muss man weiter
 * klicken“). Gesprochen wird nur, was im Admin hinterlegt ist: Die Tonspur
 * kommt fertig vom Worker (`/folie/<nr>/ton`), nichts entsteht hier.
 *
 * - `runde` im Stand steigt, wenn etwas Neues zu sprechen ist; die Folie
 *   selbst kann auch ohne neue Runde wechseln (erste Quartalsfolie: zeigen,
 *   Handy entscheidet, dann sprechen).
 * - Auf der ersten Quartalsfolie mit `kurzfassung` spricht K4ia die
 *   Kurzfassung statt des Folientexts.
 * - Pause und Fortsetzen kommen als `befehl` mit steigender Nummer.
 * - In der Fragerunde bleibt die letzte Folie stehen; die Antworten aus dem
 *   Chat (`/verlauf`) stehen unten und werden vorgelesen.
 * - **Automatik** (Flo, 28.10.): Ist sie am Handy an, meldet der Bildschirm
 *   nach dem Ende der Tonspur plus Pause `auto-weiter` — Folien ohne Text
 *   nach einer festen Zeit. Ob es wirklich weitergeht, entscheidet der Worker
 *   (nicht an der Entscheidung, nicht in die Fragerunde).
 *
 * Der Schlüssel `k` kommt aus der Adresse im Admin. Ohne ihn gibt der Worker
 * keine Folie her — der Bildschirm sagt dann, welche Adresse zu öffnen ist.
 */
export function PraesentationPage({ schluessel }: { schluessel: string }) {
  const [folien, setFolien] = useState<PraesentationFolie[] | null>(null)
  const [stand, setStand] = useState<PraesentationStand | null>(null)
  const [spricht, setSpricht] = useState(false)
  const [pausiert, setPausiert] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [zugang, setZugang] = useState<'offen' | 'verweigert'>('offen')
  const [antwort, setAntwort] = useState<PraesentationNachricht | null>(null)
  const [frage, setFrage] = useState<string | null>(null)
  const [pausen, setPausen] = useState({ nachTon: 2, ohneText: 4 })

  const audio = useRef<HTMLAudioElement | null>(null)
  /** Der Takt der Automatik: ein Timer je Runde; jede neue Runde und jeder Stopp räumt ihn ab. */
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const automatikRef = useRef(false)
  const pausenRef = useRef(pausen)
  const sprichtRef = useRef(false)
  const pausiertRef = useRef(false)
  const gespielteRunde = useRef(0)
  const letzterBefehl = useRef(0)
  const gesprocheneAntwort = useRef<string | null>(null)
  /** Die zuletzt gelesene Folie — der Takt läuft in einem Effekt und sähe den State veraltet. */
  const folieRef = useRef(0)

  // Die Folienliste: einmal, und dann ab und zu — falls das Admin gerade Bilder nachlädt.
  useEffect(() => {
    if (!schluessel) {
      setZugang('verweigert')
      return
    }
    let aktiv = true
    const laden = () =>
      void fetchPraesentationFolien(schluessel)
        .then((f) => {
          if (!aktiv) return
          setFolien(f.folien)
          const p = { nachTon: f.autoPauseSekunden, ohneText: f.autoStummSekunden }
          pausenRef.current = p
          setPausen(p)
        })
        .catch((e: unknown) => {
          if (aktiv && e instanceof Error && /404|Not found/i.test(e.message)) {
            setZugang('verweigert')
          }
        })
    laden()
    const id = setInterval(laden, 30_000)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [schluessel])

  /** Ein Audio-Element für alles; die Klick-Geste der Bühne hat es freigegeben. */
  function element(): HTMLAudioElement {
    if (!audio.current) {
      const el = new Audio()
      el.preload = 'auto'
      el.onplaying = () => {
        sprichtRef.current = true
        setSpricht(true)
      }
      el.onended = () => {
        sprichtRef.current = false
        setSpricht(false)
        planeAutoWeiter(pausenRef.current.nachTon)
      }
      el.onerror = () => {
        sprichtRef.current = false
        setSpricht(false)
      }
      audio.current = el
    }
    return audio.current
  }

  function autoAbbrechen() {
    if (autoTimer.current) clearTimeout(autoTimer.current)
    autoTimer.current = null
  }

  /**
   * Nach der Tonspur (oder bei einer stummen Folie nach fester Zeit): dem
   * Worker sagen, dass es weitergehen kann. Nur wenn die Automatik an ist;
   * ob die Folie wirklich wechselt, prüft der Worker noch einmal selbst.
   */
  function planeAutoWeiter(sekunden: number) {
    autoAbbrechen()
    if (!automatikRef.current || !schluessel) return
    autoTimer.current = setTimeout(() => {
      autoTimer.current = null
      if (!automatikRef.current) return
      void sendPraesentationAutoWeiter(schluessel).catch(() => undefined)
    }, sekunden * 1000)
  }

  function stopp() {
    autoAbbrechen()
    const el = audio.current
    if (el) {
      el.pause()
      el.removeAttribute('src')
      el.load()
    }
    sprichtRef.current = false
    pausiertRef.current = false
    setSpricht(false)
    setPausiert(false)
  }

  /** Die Tonspur holen und abspielen; „kein Text“ (404) heißt: Folie bleibt stumm. */
  async function sprechen(url: string) {
    stopp()
    setHinweis(null)
    try {
      const res = await fetch(url, { credentials: 'include', cache: 'no-store' })
      if (res.status === 404) {
        // Kein Text: Die Folie steht nur. Die Automatik lässt sie eine Weile stehen.
        planeAutoWeiter(pausenRef.current.ohneText)
        return
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; hinweis?: string }
        setHinweis([body.error ?? `HTTP ${res.status}`, body.hinweis].filter(Boolean).join(' '))
        return
      }
      const blob = await res.blob()
      const el = element()
      el.src = URL.createObjectURL(blob)
      await el.play()
    } catch (e) {
      setHinweis(e instanceof Error ? e.message : 'Die Tonspur ließ sich nicht abspielen.')
      sprichtRef.current = false
      setSpricht(false)
    }
  }

  // Jede Sekunde: Stand lesen, eigenen Stand melden, auf Runde und Befehl reagieren.
  useEffect(() => {
    if (!schluessel) return
    let aktiv = true
    const takt = async () => {
      let neu: PraesentationStand
      try {
        neu = await reportPraesentationBildschirm(schluessel, {
          folie: folieRef.current,
          spricht: sprichtRef.current,
          pausiert: pausiertRef.current,
        })
      } catch (e) {
        if (e instanceof Error && /404|Not found/i.test(e.message)) setZugang('verweigert')
        try {
          neu = await fetchPraesentationStand()
        } catch {
          return
        }
      }
      if (!aktiv) return
      setStand(neu)
      folieRef.current = neu.phase === 'bereit' ? 0 : neu.folie
      if (automatikRef.current !== neu.automatik) {
        automatikRef.current = neu.automatik
        // Aus heißt aus — auch ein schon laufender Timer meldet nichts mehr.
        if (!neu.automatik) autoAbbrechen()
      }

      if (neu.phase === 'bereit') {
        if (gespielteRunde.current !== neu.runde) {
          gespielteRunde.current = neu.runde
          stopp()
        }
        setAntwort(null)
        setFrage(null)
        gesprocheneAntwort.current = null
        return
      }

      // Etwas Neues zu sprechen: der Folientext oder die Kurzfassung.
      if (neu.runde !== gespielteRunde.current) {
        gespielteRunde.current = neu.runde
        letzterBefehl.current = neu.befehl.nr
        if (neu.phase === 'laeuft') {
          void sprechen(
            praesentationTonUrl(neu.kurzfassung ? 'kurzfassung' : { folie: neu.folie }, schluessel),
          )
        } else {
          stopp()
        }
      }

      if (neu.befehl.nr !== letzterBefehl.current) {
        letzterBefehl.current = neu.befehl.nr
        const el = audio.current
        if (neu.befehl.art === 'pause' && el && !el.paused) {
          el.pause()
          pausiertRef.current = true
          setPausiert(true)
          autoAbbrechen()
        }
        if (neu.befehl.art === 'fortsetzen' && el && pausiertRef.current) {
          pausiertRef.current = false
          setPausiert(false)
          void el.play().catch(() => undefined)
        }
      }
    }
    void takt()
    const id = setInterval(() => void takt(), POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [schluessel])

  // Fragerunde: die neueste Antwort zeigen und vorlesen.
  const fragerunde = stand?.phase === 'fragen'
  useEffect(() => {
    if (!fragerunde || !schluessel) return
    let aktiv = true
    const lesen = async () => {
      let nachrichten: PraesentationNachricht[]
      try {
        nachrichten = (await fetchPraesentationVerlauf(schluessel)).nachrichten
      } catch {
        return
      }
      if (!aktiv) return
      const letzteAntwort = [...nachrichten].reverse().find((n) => n.role === 'assistant') ?? null
      const letzteFrage = [...nachrichten].reverse().find((n) => n.role === 'user') ?? null
      setAntwort(letzteAntwort)
      setFrage(letzteFrage?.content ?? null)
      if (letzteAntwort && letzteAntwort.id !== gesprocheneAntwort.current) {
        gesprocheneAntwort.current = letzteAntwort.id
        void sprechen(praesentationAntwortTonUrl(letzteAntwort.id, schluessel))
      }
    }
    void lesen()
    const id = setInterval(() => void lesen(), VERLAUF_POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [fragerunde, schluessel])

  // Beim Aushängen (Moduswechsel am Handy) verstummen.
  useEffect(() => {
    return () => stopp()
  }, [])

  if (zugang === 'verweigert') {
    return (
      <Leer avatar="idle" titel="Präsentation">
        <p className="text-lg text-text-muted">
          Für die Präsentation braucht der Bildschirm die Adresse aus dem Admin (Bühne → Bildschirm,
          mit Schlüssel). Bitte diese Adresse öffnen.
        </p>
      </Leer>
    )
  }

  if (!stand || !folien) {
    return <main className="min-h-screen bg-bg" data-testid="praesentation-laedt" />
  }

  if (stand.phase === 'bereit') {
    return (
      <Leer avatar="idle" titel="Präsentation">
        <p className="text-lg text-text-muted">
          {folien.length === 0
            ? 'Es sind noch keine Folien hinterlegt (Admin → Präsentation).'
            : `${folien.length} Folien bereit — das Handy startet.`}
        </p>
        {hinweis && <Hinweis text={hinweis} />}
      </Leer>
    )
  }

  const folie = folien.find((f) => f.nr === stand.folie)
  const naechste = folien.find((f) => f.nr > stand.folie && f.bild)
  const avatar = spricht && !pausiert ? 'speaking' : 'idle'

  return (
    <main
      className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-black"
      data-testid="praesentation"
      data-folie={stand.folie}
      data-phase={stand.phase}
      data-spricht={spricht && !pausiert}
    >
      {folie?.bild ? (
        <img
          key={folie.nr}
          src={praesentationBildUrl(folie.nr, { k: schluessel })}
          alt={folie.titel ? `Folie ${folie.nr}: ${folie.titel}` : `Folie ${folie.nr}`}
          className="max-h-full max-w-full object-contain"
          draggable={false}
        />
      ) : (
        <div className="flex max-w-4xl flex-col items-center gap-6 p-16 text-center text-white">
          <p className="text-2xl text-white/60">Folie {stand.folie}</p>
          <h1 className="text-5xl font-semibold">{folie?.titel ?? 'Kein Bild hinterlegt'}</h1>
        </div>
      )}
      {/* Die nächste Folie leise vorladen, damit der Wechsel nicht flackert. */}
      {naechste && (
        <link
          rel="prefetch"
          as="image"
          href={praesentationBildUrl(naechste.nr, { k: schluessel })}
        />
      )}

      {/* K4ia unten rechts, klein: Sie spricht, sie wartet, sie ist in der Pause. */}
      <div
        className="pointer-events-none absolute bottom-6 right-6 flex items-center gap-3 rounded-full bg-black/55 px-4 py-2 text-white backdrop-blur"
        aria-live="polite"
      >
        <Avatar state={avatar} size={44} showLabel={false} />
        <span className="text-sm font-medium">
          {pausiert
            ? 'Pause'
            : spricht
              ? 'K4ia spricht'
              : stand.phase === 'fragen'
                ? 'Fragen aus dem Publikum'
                : stand.entscheidung
                  ? 'Quartale oder Kurzfassung?'
                  : `Folie ${stand.folie} von ${folien.length}`}
          {stand.automatik && stand.phase === 'laeuft' && !pausiert && (
            <span className="ml-2 text-xs text-white/60" data-testid="praesentation-automatik">
              · Automatik
            </span>
          )}
        </span>
      </div>

      {stand.phase === 'fragen' && (antwort || frage) && (
        <section
          className="absolute inset-x-0 bottom-0 max-h-[45%] overflow-hidden bg-gradient-to-t from-black via-black/90 to-transparent px-16 pb-20 pt-16 text-white"
          data-testid="praesentation-antwort"
        >
          {frage && <p className="mb-3 text-xl text-white/70">„{frage}“</p>}
          {antwort && <p className="text-3xl leading-snug">{antwort.content}</p>}
        </section>
      )}

      {hinweis && (
        <div className="absolute left-6 top-6">
          <Hinweis text={hinweis} />
        </div>
      )}
    </main>
  )
}

function Hinweis({ text }: { text: string }) {
  return (
    <p className="rounded-xl bg-status-error/90 px-4 py-2 text-sm text-white" role="alert">
      {text}
    </p>
  )
}

function Leer({
  avatar,
  titel,
  children,
}: {
  avatar: 'idle' | 'speaking'
  titel: string
  children: React.ReactNode
}) {
  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-8 bg-bg p-12 text-center"
      data-testid="praesentation"
      data-phase="bereit"
    >
      <Avatar state={avatar} size={220} showLabel={false} />
      <div className="flex max-w-2xl flex-col gap-3">
        <h1 className="text-4xl font-semibold">{titel}</h1>
        {children}
      </div>
    </main>
  )
}
